import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { createNowPaymentsInvoice, fetchShippingRates, checkFirstOrder, fetchSiteConfig, validateDiscountCode } from "@/lib/api";
import { ShieldCheck, Lock, Package, Coins, Truck, CheckCircle2, AlertTriangle, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { MoleculeThumb } from "@/components/MoleculeThumb";

// EU shipping countries, must match cart region "EU"
const EU_COUNTRIES = [
    ["AT", "Austria"], ["BE", "Belgium"], ["BG", "Bulgaria"], ["HR", "Croatia"],
    ["CY", "Cyprus"], ["CZ", "Czechia"], ["DK", "Denmark"], ["EE", "Estonia"],
    ["FI", "Finland"], ["FR", "France"], ["DE", "Germany"], ["GR", "Greece"],
    ["HU", "Hungary"], ["IE", "Ireland"], ["IT", "Italy"], ["LV", "Latvia"],
    ["LT", "Lithuania"], ["LU", "Luxembourg"], ["MT", "Malta"], ["NL", "Netherlands"],
    ["PL", "Poland"], ["PT", "Portugal"], ["RO", "Romania"], ["SK", "Slovakia"],
    ["SI", "Slovenia"], ["ES", "Spain"], ["SE", "Sweden"], ["GB", "United Kingdom"],
    ["NO", "Norway"], ["CH", "Switzerland"], ["IS", "Iceland"],
];
const EU_CODES = new Set(EU_COUNTRIES.map((c) => c[0]));
const NA_CODES = new Set(["US", "CA", "GB"]);

export default function Checkout() {
    const { cart, cartTotalUsd, cartFinalUsd, bundleDiscount, formatPrice, currency, firstOrderHint, FIRST_ORDER_PCT } = useApp();
    const [submitting, setSubmitting] = useState(false);
    const [payMethod] = useState("crypto"); // CRYPTO ONLY, card payments via Stripe are disabled.
    const [shipping, setShipping] = useState(null);
    const [firstOrderEligible, setFirstOrderEligible] = useState(firstOrderHint);
    const [firstOrderChecked, setFirstOrderChecked] = useState(false);
    const [euConfig, setEuConfig] = useState({ fee_min_eur: 28, fee_max_eur: 35, eta: "5 to 7 business days" });
    const [promoInput, setPromoInput] = useState("");
    const [promo, setPromo] = useState(null); // {ok, discount_usd, code, label, ...}
    const [promoBusy, setPromoBusy] = useState(false);

    // Cart region detection, "EU", "NA", or "MIXED"
    const cartRegion = useMemo(() => {
        const regions = new Set(cart.map((i) => (i.region || "NA").toUpperCase()));
        if (regions.size > 1) return "MIXED";
        return regions.has("EU") ? "EU" : "NA";
    }, [cart]);

    const defaultCountry = cartRegion === "EU" ? "DE" : "US";
    const [form, setForm] = useState({
        name: "",
        email: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        region: "",
        postal_code: "",
        country: defaultCountry,
        phone: "",
    });

    // Reset country if cart region changes mid-session and current country no longer matches
    useEffect(() => {
        if (cartRegion === "EU" && !EU_CODES.has(form.country)) {
            setForm((f) => ({ ...f, country: "DE" }));
        } else if (cartRegion === "NA" && !NA_CODES.has(form.country)) {
            setForm((f) => ({ ...f, country: "US" }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartRegion]);

    // Fetch EU shipping config
    useEffect(() => {
        fetchSiteConfig().then((c) => { if (c?.eu_shipping) setEuConfig(c.eu_shipping); }).catch(() => {});
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    // Fetch shipping rates whenever country, postal code, or cart subtotal changes
    useEffect(() => {
        if (cart.length === 0 || isEUOrder || isMixedCart) { setShipping(null); return; }
        let cancelled = false;
        fetchShippingRates({
            country: form.country,
            subtotal_usd: cartFinalUsd,
            postal_code: form.postal_code,
            address_line1: form.address_line1,
            city: form.city,
            state: form.state,
            items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        })
            .then((r) => { if (!cancelled) setShipping(r); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [form.country, form.postal_code, form.city, form.state, form.address_line1, cartFinalUsd, cart.length]);

    const shippingOption = shipping?.options?.[0];
    const isEUOrder = cartRegion === "EU";
    const isMixedCart = cartRegion === "MIXED";
    const countryMismatch = !isMixedCart && (
        (cartRegion === "EU" && !EU_CODES.has(form.country)) ||
        (cartRegion === "NA" && !NA_CODES.has(form.country))
    );
    // EU orders use a flat €28-€35 fee. Use the average for the order total; show range to user.
    const euFeeEur = (euConfig.fee_min_eur + euConfig.fee_max_eur) / 2;
    // €1 ≈ $1.08 USD as a stable proxy (Stripe/NowPayments charge actual EUR)
    const EUR_TO_USD = 1.08;
    const euFeeUsd = +(euFeeEur * EUR_TO_USD).toFixed(2);
    const shipUsd = isEUOrder ? euFeeUsd : (shippingOption ? (shippingOption.amount_usd ?? shippingOption.amount) : 0);
    const firstOrderDiscountUsd = (firstOrderHint && firstOrderEligible) ? cartFinalUsd * FIRST_ORDER_PCT : 0;
    const subtotalAfterFirst = Math.max(0, cartFinalUsd - firstOrderDiscountUsd);
    const promoDiscountUsd = (promo && promo.ok) ? Math.min(promo.discount_usd || 0, subtotalAfterFirst) : 0;
    const subtotalAfterDiscounts = Math.max(0, subtotalAfterFirst - promoDiscountUsd);
    // Made-to-order surcharge, flat fee added once when any MTO product is in cart.
    const mtoItems = cart.filter((i) => i.made_to_order);
    const hasMto = mtoItems.length > 0;
    const mtoFeeUsd = hasMto ? Math.max(...mtoItems.map((i) => Number(i.made_to_order_fee_usd) || 50)) : 0;
    const mtoLeadTime = hasMto ? (mtoItems[0].made_to_order_lead_time || "4 to 6 business days") : "";
    const grandTotalUsd = subtotalAfterDiscounts + mtoFeeUsd + shipUsd;
    const freeShipRemainingUsd = shipping ? Math.max(0, 150 - subtotalAfterDiscounts) : 0;

    const applyPromo = async () => {
        const code = promoInput.trim().toUpperCase();
        if (!code) return;
        setPromoBusy(true);
        try {
            const r = await validateDiscountCode(code, subtotalAfterFirst);
            if (r.ok) { setPromo(r); toast.success(`${code} applied · ${r.type === "fixed" ? `$${r.value} off` : `${r.value}% off`}`); }
            else { setPromo({ ok: false, reason: r.reason }); toast.error(r.reason || "Invalid code"); }
        } catch { toast.error("Could not validate code"); setPromo({ ok: false, reason: "Network error" }); }
        setPromoBusy(false);
    };
    const clearPromo = () => { setPromo(null); setPromoInput(""); };

    // Validate first-order against email (debounced), server is the source of truth
    useEffect(() => {
        if (!firstOrderHint) return;
        const e = (form.email || "").trim().toLowerCase();
        if (!e || !e.includes("@") || !e.includes(".")) { setFirstOrderChecked(false); return; }
        const t = setTimeout(() => {
            checkFirstOrder(e)
                .then((r) => { setFirstOrderEligible(!!r.eligible); setFirstOrderChecked(true); })
                .catch(() => {});
        }, 500);
        return () => clearTimeout(t);
    }, [form.email, firstOrderHint]);

    if (cart.length === 0) {
        return (
            <div className="bg-ink-base min-h-screen">
                <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                    <p className="text-ink-dim">Your cart is empty.</p>
                    <Link to="/shop" className="mt-6 inline-block text-neon underline">Browse products</Link>
                </div>
            </div>
        );
    }

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (isMixedCart) {
            toast.error("Cart contains items from both EU and North-America warehouses. Please place them as separate orders.");
            return;
        }
        if (countryMismatch) {
            if (cartRegion === "EU") {
                toast.error("EU warehouse products can only ship to European addresses. Please choose an EU country.");
            } else {
                toast.error("We ship to United States, Canada, and United Kingdom only. Please choose one of those countries.");
            }
            return;
        }
        const required = ["name", "email", "address_line_1", "city", "region", "postal_code", "country"];
        for (const f of required) {
            if (!form[f]) { toast.error(`Please fill ${f.replace("_", " ")}`); return; }
        }
        setSubmitting(true);
        try {
            // CRYPTO-ONLY checkout, card payments via Stripe are disabled.
            const cryptoPayload = {
                items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
                origin_url: window.location.origin,
                currency: "usd",
                shipping: {
                    full_name: form.name,
                    email: form.email,
                    phone: form.phone,
                    address_line1: form.address_line_1,
                    address_line2: form.address_line_2,
                    city: form.city,
                    state: form.region,
                    postal_code: form.postal_code,
                    country: form.country,
                },
                shipping_cost_usd: shipUsd,
                discount_code: (promo && promo.ok) ? promo.code : null,
            };
            const res = await createNowPaymentsInvoice(cryptoPayload);
            if (res.invoice_url) {
                window.location.href = res.invoice_url;
                return;
            }
            toast.error("Could not create crypto invoice");
            setSubmitting(false);
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Checkout failed");
            setSubmitting(false);
        }
    };

    const inputCls = "w-full border border-ink-strong bg-ink-surface text-ink px-4 py-3 text-sm focus:outline-none focus:border-neon transition-colors placeholder:text-ink-mute";

    return (
        <div className="bg-ink-base text-ink min-h-screen" data-testid="checkout-page">
            <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-16">
                <p className="section-num mb-4">/ CHECKOUT · SECURE</p>
                <h1 className="font-display text-5xl sm:text-7xl font-bold text-ink tracking-tight mb-14 leading-[0.92]">Shipping &<br /><span className="text-neon">payment.</span></h1>

                <form onSubmit={submit} className="grid md:grid-cols-[1fr_420px] gap-12">
                    <div className="space-y-10">
                        {isMixedCart && (
                            <div className="p-4 border border-red-500/60 bg-red-500/5 flex items-start gap-3" data-testid="mixed-cart-warning">
                                <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                <div className="text-sm text-red-200">
                                    <strong className="block text-red-300 font-display">Mixed-warehouse cart</strong>
                                    Your cart contains both EU-warehouse and North-America items. They ship from different facilities and must be ordered separately. Please remove items from one warehouse before checking out.
                                </div>
                            </div>
                        )}
                        {!isMixedCart && cartRegion === "EU" && (
                            <div className="p-4 border border-gold/40 bg-gold/5 flex items-start gap-3" data-testid="eu-warehouse-banner">
                                <Truck className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                                <div className="text-sm text-ink-dim">
                                    <strong className="block text-gold font-display">🇪🇺 Ships from EU Warehouse</strong>
                                    Flat shipping €{euConfig.fee_min_eur}-€{euConfig.fee_max_eur} · ETA {euConfig.eta} · ships only to European addresses.
                                </div>
                            </div>
                        )}
                        {countryMismatch && (
                            <div className="p-4 border border-red-500/60 bg-red-500/5 flex items-start gap-3" data-testid="country-mismatch-warning">
                                <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                <div className="text-sm text-red-200">
                                    {cartRegion === "EU"
                                        ? "EU-warehouse products can only be shipped to European addresses. Please select an EU country."
                                        : "North-America products can only be shipped to US or Canadian addresses."}
                                </div>
                            </div>
                        )}
                        <div>
                            <h3 className="overline text-neon mb-5">01 · CONTACT</h3>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="overline text-[10px] text-ink-mute mb-2 block">Full name</span>
                                    <input className={inputCls} value={form.name} onChange={set("name")} data-testid="checkout-name" required />
                                </label>
                                <label className="block">
                                    <span className="overline text-[10px] text-ink-mute mb-2 block">Email</span>
                                    <input type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="checkout-email" required />
                                </label>
                            </div>
                        </div>

                        <div>
                            <h3 className="overline text-neon mb-5">02 · SHIPPING ADDRESS</h3>
                            <div className="space-y-3">
                                <label className="block">
                                    <span className="overline text-[10px] text-ink-mute mb-2 block">Street address</span>
                                    <input className={inputCls} value={form.address_line_1} onChange={set("address_line_1")} data-testid="checkout-address_line_1" required />
                                </label>
                                <label className="block">
                                    <span className="overline text-[10px] text-ink-mute mb-2 block">Apartment, suite (optional)</span>
                                    <input className={inputCls} value={form.address_line_2} onChange={set("address_line_2")} data-testid="checkout-address_line_2" />
                                </label>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <label className="block">
                                        <span className="overline text-[10px] text-ink-mute mb-2 block">City</span>
                                        <input className={inputCls} value={form.city} onChange={set("city")} data-testid="checkout-city" required />
                                    </label>
                                    <label className="block">
                                        <span className="overline text-[10px] text-ink-mute mb-2 block">State / Province</span>
                                        <input className={inputCls} value={form.region} onChange={set("region")} data-testid="checkout-region" required />
                                    </label>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <label className="block">
                                        <span className="overline text-[10px] text-ink-mute mb-2 block">Postal / ZIP</span>
                                        <input className={inputCls} value={form.postal_code} onChange={set("postal_code")} data-testid="checkout-postal_code" required />
                                    </label>
                                    <label className="block">
                                        <span className="overline text-[10px] text-ink-mute mb-2 block">Country</span>
                                        <select
                                            value={form.country}
                                            onChange={set("country")}
                                            className={inputCls}
                                            data-testid="checkout-country"
                                        >
                                            {cartRegion === "EU" ? (
                                                EU_COUNTRIES.map(([code, name]) => (
                                                    <option key={code} value={code}>{name}</option>
                                                ))
                                            ) : (
                                                <>
                                                    <option value="US">United States</option>
                                                    <option value="CA">Canada</option>
                                                    <option value="GB">United Kingdom</option>
                                                </>
                                            )}
                                        </select>
                                    </label>
                                </div>
                                <label className="block">
                                    <span className="overline text-[10px] text-ink-mute mb-2 block">Phone (optional)</span>
                                    <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="checkout-phone" />
                                </label>
                            </div>
                        </div>

                        <div>
                            <h3 className="overline text-neon mb-5">03 · PAYMENT METHOD</h3>
                            <div className="p-5 border border-neon bg-ink-surface" data-testid="payment-method-crypto-only">
                                <div className="flex items-start gap-4">
                                    <Coins className="w-8 h-8 text-gold shrink-0 mt-0.5" strokeWidth={1.4} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-baseline gap-2">
                                            <span className="font-display text-lg font-bold text-ink">Crypto checkout</span>
                                            <span className="overline text-neon text-[10px]">USDC · USDT · BTC · ETH · XRP</span>
                                        </div>
                                        <p className="text-xs text-ink-dim mt-2 leading-relaxed">
                                            Pay in any major cryptocurrency through our secure NowPayments processor.
                                            Prices stay in USD, the network converts at the live exchange rate when you pay.
                                            Order is confirmed automatically once the network confirms your transaction
                                            (usually 5 to 30 minutes depending on the coin).
                                        </p>
                                        <Link to="/how-to-pay-with-crypto"
                                            className="inline-flex items-center gap-1.5 mt-3 text-xs font-mono uppercase tracking-wider text-neon hover:underline"
                                            data-testid="crypto-guide-link">
                                            <BookOpen className="w-3.5 h-3.5" /> New to crypto? Read our 3-minute guide →
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-5 border border-ink-strong bg-ink-surface space-y-2 text-xs text-ink-dim" data-testid="shipping-block">
                            <div className="flex items-center gap-2">
                                <Truck className="w-3.5 h-3.5 text-neon" />
                                <span className="font-semibold text-ink">{isEUOrder ? "Discreet Shipping from EU Warehouse" : "Discreet Shipping from Canadian Warehouse"}</span>
                            </div>
                            {isEUOrder ? (
                                <>
                                    <div className="flex items-center gap-2"><Package className="w-3.5 h-3.5 text-neon" /><span>Local EU courier · €{euConfig.fee_min_eur}-€{euConfig.fee_max_eur}</span></div>
                                    <div className="flex items-center gap-2"><span className="overline text-[9px] text-gold">ETA:</span><span>{euConfig.eta}</span></div>
                                    <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-neon" /><span>Payment securely processed by NowPayments.</span></div>
                                </>
                            ) : shippingOption ? (
                                <>
                                    <div className="flex items-center gap-2"><Package className="w-3.5 h-3.5 text-neon" /><span>{shippingOption.carrier} · {shippingOption.service}</span></div>
                                    <div className="flex items-center gap-2"><span className="overline text-[9px] text-gold">ETA:</span><span>{shippingOption.eta}</span></div>
                                    <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-neon" /><span>Payment securely processed by NowPayments.</span></div>
                                </>
                            ) : (
                                <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-neon" /><span>Payment securely processed by NowPayments.</span></div>
                            )}
                        </div>
                    </div>

                    <aside className="md:sticky md:top-32 md:self-start">
                        <div className="border border-ink-strong p-6 bg-ink-surface" data-testid="checkout-summary">
                            <h3 className="overline text-neon mb-5">ORDER</h3>
                            <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                                {cart.map((i) => (
                                    <div key={i.product_id} className="flex gap-3 text-sm">
                                        <div className="w-14 h-14 border border-ink bg-ink-base shrink-0 overflow-hidden">
                                            <MoleculeThumb molecule={i.molecule} className="w-full h-full" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-ink truncate text-sm">{i.name}</p>
                                            <p className="font-mono text-xs text-ink-mute">{i.dosage} × {i.quantity}</p>
                                        </div>
                                        <span className="font-mono text-sm font-semibold text-ink">{formatPrice(i.price_usd * i.quantity)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 pt-5 border-t border-ink space-y-2 text-sm text-ink-dim">
                                <div className="flex justify-between"><span>Subtotal</span><span className="font-mono text-ink">{formatPrice(cartTotalUsd)}</span></div>
                                <BundleHints cart={cart} />
                                {bundleDiscount.discount_usd > 0 && bundleDiscount.applied.map((a) => (
                                    <div key={a.group} className="flex justify-between text-neon" data-testid={`checkout-bundle-${a.group}`}>
                                        <span className="font-mono text-xs">★ {a.label} · {Math.round(a.pct * 100)}% OFF</span>
                                        <span className="font-mono">−{formatPrice(a.discount_usd)}</span>
                                    </div>
                                ))}
                                {firstOrderDiscountUsd > 0 && (
                                    <div className="flex justify-between text-gold" data-testid="checkout-first-order">
                                        <span className="font-mono text-xs">⌬ FIRST ORDER · {Math.round(FIRST_ORDER_PCT * 100)}% OFF</span>
                                        <span className="font-mono">−{formatPrice(firstOrderDiscountUsd)}</span>
                                    </div>
                                )}
                                {firstOrderHint && firstOrderChecked && !firstOrderEligible && (
                                    <div className="text-[11px] font-mono text-ink-mute" data-testid="first-order-revoked">
                                        ★ Welcome back, first-order discount applies to new researchers only.
                                    </div>
                                )}

                                {/* PROMO CODE INPUT */}
                                <div className="pt-3 mt-2 border-t border-ink" data-testid="promo-code-block">
                                    {!(promo && promo.ok) ? (
                                        <div>
                                            <p className="overline text-[10px] text-ink-mute mb-2">PROMO / PARTNERSHIP CODE</p>
                                            <div className="flex gap-2">
                                                <input
                                                    value={promoInput}
                                                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
                                                    placeholder="ENTER CODE"
                                                    className="flex-1 border border-ink-strong bg-ink-base text-ink px-3 py-2 text-xs font-mono uppercase tracking-wider focus:outline-none focus:border-neon"
                                                    data-testid="promo-code-input"
                                                />
                                                <button type="button" onClick={applyPromo} disabled={promoBusy || !promoInput.trim()}
                                                    className="bg-gold hover:bg-gold/90 text-black px-4 py-2 text-[10px] font-bold uppercase tracking-wider disabled:opacity-40"
                                                    data-testid="promo-apply-btn">
                                                    {promoBusy ? "…" : "APPLY"}
                                                </button>
                                            </div>
                                            {promo && !promo.ok && <p className="mt-2 text-[10px] font-mono text-red-400" data-testid="promo-error">✗ {promo.reason}</p>}
                                        </div>
                                    ) : (
                                        <div className="flex justify-between items-baseline" data-testid="promo-applied">
                                            <div>
                                                <span className="font-mono text-xs text-gold">★ PROMO · {promo.code}</span>
                                                {promo.label && <span className="ml-2 text-[10px] text-ink-mute">{promo.label}</span>}
                                                <button type="button" onClick={clearPromo} className="ml-2 text-[10px] text-ink-mute hover:text-red-400 underline" data-testid="promo-remove-btn">remove</button>
                                            </div>
                                            <span className="font-mono text-gold">−{formatPrice(promoDiscountUsd)}</span>
                                        </div>
                                    )}
                                </div>

                                {isEUOrder ? (
                                    <div className="flex justify-between" data-testid="shipping-line-eu">
                                        <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-gold" /> EU shipping ({euConfig.eta})</span>
                                        <span className="font-mono text-ink">€{euConfig.fee_min_eur}-€{euConfig.fee_max_eur}</span>
                                    </div>
                                ) : shippingOption ? (
                                    <div className="flex justify-between" data-testid="shipping-line">
                                        <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-neon" /> Shipping ({shippingOption.eta})</span>
                                        <span className={`font-mono ${shippingOption.free ? "text-neon" : "text-ink"}`}>
                                            {shippingOption.free ? "FREE" : formatPrice(shippingOption.amount_usd)}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="flex justify-between"><span>Shipping</span><span className="text-neon font-mono">Calculating…</span></div>
                                )}
                                {hasMto && (
                                    <>
                                        <div className="flex justify-between text-amber-400" data-testid="checkout-mto-fee">
                                            <span className="font-mono text-xs flex items-center gap-1.5">⚒ MADE-TO-ORDER SHIPPING</span>
                                            <span className="font-mono">+{formatPrice(mtoFeeUsd)}</span>
                                        </div>
                                        <div className="p-2.5 border border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-400 leading-relaxed font-mono" data-testid="checkout-mto-banner">
                                            ⚒ <strong>{mtoLeadTime}</strong> production lead time, kit produced fresh from lab stock. Standard shipping ETA above is added on top of this.
                                        </div>
                                    </>
                                )}
                                {freeShipRemainingUsd > 0 && freeShipRemainingUsd < 50 && (
                                    <div className="text-[11px] font-mono text-gold" data-testid="free-ship-nudge">
                                        ★ Add {formatPrice(freeShipRemainingUsd)} more for FREE shipping
                                    </div>
                                )}
                                {payMethod === "crypto" && <div className="flex justify-between"><span>Payment</span><span className="font-mono text-gold">Crypto · USD priced</span></div>}
                            </div>
                            <div className="mt-5 pt-5 border-t border-ink flex justify-between items-end">
                                <span className="overline text-ink">TOTAL</span>
                                <span className="font-display text-2xl font-bold text-ink" data-testid="checkout-total">
                                    {payMethod === "crypto" ? `$${grandTotalUsd.toFixed(2)} USD` : formatPrice(grandTotalUsd)}
                                </span>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting || isMixedCart || countryMismatch}
                                className="w-full mt-6 bg-neon hover:bg-neon-glow text-black px-6 py-4 font-bold tracking-wide inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid="pay-button"
                            >
                                <Lock className="w-4 h-4" /> {submitting ? "REDIRECTING…" : isMixedCart ? "MIXED CART · CANNOT CHECKOUT" : countryMismatch ? "REGION MISMATCH" : "PAY WITH CRYPTO"}
                            </button>

                            <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-ink-mute text-center font-mono">For Research Use Only</p>
                        </div>
                    </aside>
                </form>
            </div>
        </div>
    );
}


/**
 * BundleHints, for each unique bundle key in the cart, look at the next un-reached tier
 * and tell the customer exactly how many more vials/kits they need to unlock the discount.
 * Bundle tiers come from the server (`product.bundle_tiers`) so editing them is one place.
 */
function BundleHints({ cart }) {
    const [tiersByKey, setTiersByKey] = React.useState({});

    React.useEffect(() => {
        const slugs = Array.from(new Set(cart.map((i) => i.slug).filter(Boolean)));
        if (!slugs.length) { setTiersByKey({}); return; }
        let cancelled = false;
        Promise.all(slugs.map(async (slug) => {
            try {
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${slug}`);
                if (!r.ok) return null;
                const p = await r.json();
                return { key: p.variant_group || p.id, tiers: p.bundle_tiers || [] };
            } catch { return null; }
        })).then((results) => {
            if (cancelled) return;
            const map = {};
            for (const r of results.filter(Boolean)) {
                if (!map[r.key]) map[r.key] = r;
            }
            setTiersByKey(map);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart.map((i) => i.slug + ":" + i.quantity).join("|")]);

    const hints = [];
    for (const [key, info] of Object.entries(tiersByKey)) {
        const qty = cart
            .filter((i) => i.variant_group === key || i.product_id === key)
            .reduce((s, i) => s + i.quantity, 0);
        if (!qty) continue;
        const tiers = [...(info.tiers || [])].sort((a, b) => a.min_qty - b.min_qty);
        const next = tiers.find((t) => qty < t.min_qty);
        if (!next) continue;
        const need = next.min_qty - qty;
        const pct = Math.round(next.discount_pct * 100);
        hints.push({
            key,
            need,
            pct,
            label: next.label,
            unit: key === "hgh" ? "kit" : "vial",
        });
    }

    if (hints.length === 0) return null;
    return (
        <div className="my-2 space-y-1.5" data-testid="bundle-hints">
            {hints.map((h) => (
                <div key={h.key} className="text-[11px] font-mono text-gold bg-gold/5 border border-gold/30 px-3 py-2" data-testid={`bundle-hint-${h.key}`}>
                    💡 Add <span className="font-bold">{h.need} more {h.unit}{h.need === 1 ? "" : "s"}</span> to unlock <span className="font-bold">{h.pct}% OFF</span> · {h.label}
                </div>
            ))}
        </div>
    );
}

